import { z } from 'zod';

/* The floor every realm shares. Declared in lists.ts beside the admin roster
   it was written for; players and partners both re-export it rather than
   picking their own number. */
import { ADMIN_PASSWORD_MINIMUM } from './lists.ts';

/**
 * Sign-in, exactly as the Login and 2FA screen walks it:
 * credentials → two-factor → signed in.
 */

export const signInRequest = z.object({
  email: z.email('Enter a valid work email address.').max(320),
  password: z.string().min(1, 'Enter your password.').max(1024),
  /** "Trust this device 30 days" */
  trustDevice: z.boolean().default(false),
});
export type SignInRequest = z.infer<typeof signInRequest>;

/**
 * What the first step returns.
 *
 * Three shapes, not two: an admin who has never enrolled in two-factor gets
 * `TWO_FACTOR_ENROLMENT_REQUIRED` instead of `TWO_FACTOR_REQUIRED` — there is
 * no authenticator yet to challenge. Both are a SUCCESS, not an error: the
 * credentials were correct and the flow is proceeding to its second step.
 * Modelling either as a failure would make a client treat a normal sign-in
 * as something gone wrong. Every OTHER outcome (locked out, bad credentials,
 * suspended) is an ordinary envelope failure and never reaches this union —
 * confirmed against every branch of the route, not assumed from the two
 * cases a client happens to already handle.
 *
 * Every branch, and the nested `session` object, is `strictObject` rather
 * than `object`. `assertShape` at the route boundary returns the PARSED
 * value, and a plain `object` strips a field it doesn't declare instead of
 * rejecting it — silent in exactly the direction a missing field is loud in.
 * That asymmetry is tolerable on a route where drift is unlikely; it is not
 * tolerable on THIS one, whose only reason to exist is that route and
 * contract must not drift. Strict makes a field someone adds here (say,
 * `mustChangePassword` on `session`) fail until this file is updated to
 * declare it, rather than vanish from the response with nothing logged.
 */

/**
 * What `completeSignIn` returns, wherever it is called.
 *
 * Named separately from `signInResult` because a second endpoint
 * (`POST /api/auth/enrol-2fa/confirm`) sends this SAME shape plus one more
 * field — see `confirmTwoFactorEnrolmentResult` below — and that second
 * schema needs to reuse this one rather than restate nine fields a second
 * time for them to silently drift apart.
 */
const signedInSession = z.strictObject({
  displayName: z.string(),
  role: z.string(),
  /** Empty means every brand — "scope: all 3 brands". */
  brands: z.array(z.object({ id: z.string(), name: z.string() })),
  permissions: z.array(z.string()),
  expiresAt: z.iso.datetime(),
  idleTimeoutMinutes: z.number().int().positive(),
  deviceTrusted: z.boolean(),
  lastSignInAt: z.iso.datetime().nullable(),
  lastSignInLabel: z.string().nullable(),
});

export const signInResult = z.discriminatedUnion('status', [
  z.strictObject({
    status: z.literal('TWO_FACTOR_ENROLMENT_REQUIRED'),
    /** Short-lived, single-purpose. It cannot be used as a session. */
    challengeToken: z.string(),
    /** Echoed so the enrolment step can say who it is for. */
    email: z.email(),
    /** Seconds until the challenge expires — the screen's countdown. */
    expiresIn: z.number().int().positive(),
    /**
     * No `devTotpAvailable` here, unlike `TWO_FACTOR_REQUIRED` below.
     *
     * The dev helper shows the code an ALREADY-enrolled authenticator would
     * be displaying. An admin on this branch has no authenticator yet — there
     * is nothing for the helper to read, in any environment.
     */
  }),
  z.strictObject({
    status: z.literal('TWO_FACTOR_REQUIRED'),
    /** Short-lived, single-purpose. It cannot be used as a session. */
    challengeToken: z.string(),
    /** Echoed so the OTP step can say who it is for. */
    email: z.email(),
    /** Seconds until the challenge expires — the screen's countdown. */
    expiresIn: z.number().int().positive(),
    /**
     * Whether this deployment offers the develop code helper.
     *
     * Sent by the SERVER rather than compiled into the bundle. The console is
     * one build promoted through environments, so a build-time flag would have
     * to be right in three places at once; the API already knows its own
     * NODE_ENV, and it is the process that decides whether the route exists at
     * all. One fact, one owner — a production API cannot advertise a helper it
     * did not register.
     */
    devTotpAvailable: z.boolean().optional(),
  }),
  z.strictObject({
    status: z.literal('SIGNED_IN'),
    session: signedInSession,
  }),
]);
export type SignInResult = z.infer<typeof signInResult>;

export const verifyTwoFactorRequest = z.object({
  challengeToken: z.string().min(1),
  /** Six digits, or a recovery code in AAAAA-BBBBB form. */
  code: z.string().min(6).max(16),
});
export type VerifyTwoFactorRequest = z.infer<typeof verifyTwoFactorRequest>;

/**
 * The develop two-factor helper.
 *
 * Keyed by the CHALLENGE, never by an email. A caller must already have passed
 * the password step for the account it names, so this widens nobody's reach: it
 * hands back the code the authenticator would be showing to a person who has
 * already proven the first factor. Accepting an email instead would have turned
 * it into an oracle for any enrolled admin.
 */
export const devTotpCodeRequest = z.object({
  challengeToken: z.string().min(1),
});
export type DevTotpCodeRequest = z.infer<typeof devTotpCodeRequest>;

export const devTotpCodeResult = z.object({
  code: z.string().regex(/^\d{6}$/),
  /** Whose code it is — shown so a helper cannot quietly fill the wrong one. */
  email: z.email(),
  /** Seconds left in this 30-second step, so the UI can say when it dies. */
  secondsRemaining: z.number().int().nonnegative(),
});
export type DevTotpCodeResult = z.infer<typeof devTotpCodeResult>;

export const sessionSummary = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.email(),
  role: z.string(),
  isSuper: z.boolean(),
  brands: z.array(z.object({ id: z.string(), name: z.string() })),
  permissions: z.array(z.string()),
  mfa: z.boolean(),
  expiresAt: z.iso.datetime(),
  idleTimeoutMinutes: z.number().int().positive(),
});
export type SessionSummary = z.infer<typeof sessionSummary>;

/** Ten single-use codes, shown once. Whoever (re)issues a factor sends these. */
const recoveryCodes = z.array(z.string());

/** TOTP enrolment, shown as a QR plus ten single-use recovery codes. */
export const enrolTwoFactorResult = z.object({
  /** otpauth:// URI the QR encodes. */
  uri: z.string(),
  /** Shown for manual entry when a camera is not available. */
  secret: z.string(),
  recoveryCodes,
});
export type EnrolTwoFactorResult = z.infer<typeof enrolTwoFactorResult>;

/**
 * `POST /api/auth/enrol-2fa/confirm` — the COMPLETION step, not the setup
 * step above. An admin who just proved possession of their authenticator is
 * signed in AND shown their recovery codes in the same response, because
 * this is the only moment those codes are ever shown.
 *
 * Deliberately its own type, not `enrolTwoFactorResult` with more fields
 * bolted on: that one is `{uri, secret, recoveryCodes}`, returned by
 * `/enrol-2fa/start` before any factor is confirmed and before a session
 * exists at all. This one is `signInResult`'s `SIGNED_IN` shape — reusing
 * `signedInSession` rather than restating it — plus `recoveryCodes`
 * alongside `status`/`session`, not nested inside either. A name that only
 * added "confirm" to the setup step's name would invite exactly the
 * confusion this comment exists to prevent.
 *
 * To be asserted at `auth/routes.ts:1157` under md-gp-47 — no consumer yet.
 * The base `SIGNED_IN` shape will be asserted inside `completeSignIn`
 * itself, and THIS schema separately at `:1157`, after `recoveryCodes` is
 * spread on. Two assertions, not one, because `:1157`'s payload is a
 * superset only that one call site produces — asserting just the base
 * shape there would leave `recoveryCodes` drifting unchecked, the exact
 * gap this schema exists to close.
 */
export const confirmTwoFactorEnrolmentResult = z.strictObject({
  status: z.literal('SIGNED_IN'),
  session: signedInSession,
  recoveryCodes,
});
export type ConfirmTwoFactorEnrolmentResult = z.infer<typeof confirmTwoFactorEnrolmentResult>;

export const partnerSignInRequest = signInRequest;
export type PartnerSignInRequest = SignInRequest;

/* ══════════════════════════════════════════════════════════════════════════
   AN ADMINISTRATOR WHO CANNOT GET IN
   ══════════════════════════════════════════════════════════════════════════

   Players have `/api/player/password-reset/*`. Partners have
   `/api/{agent,affiliate}/password-reset`. Administrators — including the
   person holding `funds.approve`, whose lockout stops a payout queue — had
   nothing, and the sign-in screen carried no "Forgot password?" link that
   `docs/DESIGN_INVENTORY.md` §3.1 specifies. The answer was "find another
   Super Admin", which is no answer at 2am or for an operator with one.
   ══════════════════════════════════════════════════════════════════════════ */

export const requestAdminPasswordResetRequest = z.object({
  email: z.email('Enter a valid email address.').max(320),
});
export type RequestAdminPasswordResetRequest = z.infer<
  typeof requestAdminPasswordResetRequest
>;

/**
 * What asking returns — AND WHAT IT MUST NOT.
 *
 * One literal object, the shape `requestPlayerPasswordResetResult` argues for
 * at length. No `found`, no display name, no message reference. Every field
 * has the same value for an address with an account, one without, one that is
 * suspended, and one whose account has no confirmed second factor.
 *
 * That last case is the interesting one and it is the reason this route exists
 * in the shape it does — see `adminResetRefusesWithoutAFactor` below.
 *
 * `mailDelivery` is a property of the DEPLOYMENT, not of the account: computed
 * from `MAIL_OUTBOUND_MODE` and `MAIL_ALLOWLIST` against the address that was
 * typed, which answers identically whether or not an admin exists behind it.
 * It is here because a screen printing "check your inbox" over an environment
 * that sends no mail tells somebody to wait for something that is not coming.
 */
export const requestAdminPasswordResetResult = z.object({
  status: z.literal('RECEIVED'),
  mailDelivery: z.enum(['ENQUEUED', 'SUPPRESSED']),
});
export type RequestAdminPasswordResetResult = z.infer<
  typeof requestAdminPasswordResetResult
>;

/**
 * THE LOCK THAT STOPS AN INBOX BECOMING A CONSOLE.
 *
 * Recorded as a named constant so the rule is greppable from both the route
 * and its test, because it is not obvious and it is the one that matters.
 *
 * `features/auth/SignIn.tsx` records the hazard: after a factor reset, a
 * password alone reaches the ENROL step, and the ENROL step mints a verified
 * session. So on an account with NO CONFIRMED SECOND FACTOR, a password is
 * sufficient for full access — and a reset link delivered to an inbox would
 * therefore chain straight to it.
 *
 * The request route refuses those accounts, and answers identically to an
 * unknown address so the refusal cannot be probed for. An administrator who
 * has lost both their password and their authenticator goes to a Super Admin,
 * which is the designed path: `access.manage` → reset 2FA, with `refuseSelf`
 * already forbidding it on your own account.
 */
export const adminResetRefusesWithoutAFactor = true as const;

/**
 * Spending the link.
 *
 * The token arrives in a POST BODY and never a query string: the link puts it
 * in the URL FRAGMENT, which browsers do not transmit, so it cannot reach an
 * access log, a proxy log or a `Referer` header.
 */
export const completeAdminPasswordResetRequest = z.object({
  token: z.string().min(20, 'That reset link is incomplete.').max(200),
  password: z
    .string()
    .min(
      ADMIN_PASSWORD_MINIMUM,
      `Choose at least ${ADMIN_PASSWORD_MINIMUM} characters — this account can move money.`,
    )
    .max(1024),
});
export type CompleteAdminPasswordResetRequest = z.infer<
  typeof completeAdminPasswordResetRequest
>;

/**
 * What completing one returns.
 *
 * NO SESSION IS MINTED, and `signInRequired` says so to the screen. Holding
 * the link proves control of the inbox; signing in is a separate act with its
 * own attempt row and its own SECOND FACTOR. Minting a session here would skip
 * both — which is the whole chain this is built to refuse.
 *
 * `sessionsEnded` is reported rather than assumed. The reset bumps
 * `token_version`, stopping every cookie minted before it — including whoever
 * caused the reset to be needed.
 *
 * `factorUntouched` is always true and is in the response on purpose: the
 * screen tells the administrator they will still need their authenticator, so
 * somebody who has lost BOTH learns it now rather than at the sign-in wall.
 */
export const completeAdminPasswordResetResult = z.object({
  status: z.literal('RESET'),
  displayName: z.string(),
  email: z.string(),
  sessionsEnded: z.number().int().nonnegative(),
  /* Trusted devices are a SEPARATE credential from a session — "trust this
     device 30 days" skips the second factor — so the count is reported
     separately rather than folded into `sessionsEnded`. The screen tells the
     person their saved devices will ask for a code again, which is a visible
     consequence they would otherwise meet by surprise. */
  trustedDevicesEnded: z.number().int().nonnegative(),
  signInRequired: z.literal(true),
  factorUntouched: z.literal(true),
});
export type CompleteAdminPasswordResetResult = z.infer<
  typeof completeAdminPasswordResetResult
>;

/** What the reset screen shows before asking for a new password. */
export const previewAdminPasswordResetRequest = z.object({
  token: z.string().min(20, 'That reset link is incomplete.').max(200),
});
export type PreviewAdminPasswordResetRequest = z.infer<
  typeof previewAdminPasswordResetRequest
>;

export const previewAdminPasswordResetResult = z.object({
  email: z.string(),
  displayName: z.string(),
  role: z.string(),
  expiresAt: z.iso.datetime(),
  passwordMinimum: z.number().int().positive(),
});
export type PreviewAdminPasswordResetResult = z.infer<
  typeof previewAdminPasswordResetResult
>;
